<template>
  <transition name="app-modal-fade">
    <div v-if="showModal" class="app-modal-backdrop" @click.self="onBackdropClick">
        <div
          class="app-modal"
          :class="modalClasses"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
        >
          <div v-if="!hideHeader" class="app-modal__header">
            <h5 :id="titleId" class="app-modal__title">{{ title }}</h5>
            <slot name="modal-header" />
            <button
              v-if="$slots['modal-header'] || !hideHeader"
              class="app-modal__close"
              @click="close"
              aria-label="Close"
            >&times;</button>
          </div>
          <div v-if="!hideHeader && title && !$slots['modal-header']" class="app-modal__header">
            <h5 :id="titleId" class="app-modal__title">{{ title }}</h5>
            <button class="app-modal__close" @click="close" aria-label="Close">&times;</button>
          </div>
          <div v-else-if="!hideHeader && $slots['modal-header']" class="app-modal__header">
            <slot name="modal-header" />
            <button class="app-modal__close" @click="close" aria-label="Close">&times;</button>
          </div>
          <div class="app-modal__body" :class="{ 'app-modal__body--scrollable': scrollable }">
            <slot />
          </div>
          <div v-if="!hideFooter" class="app-modal__footer">
            <slot name="modal-footer">
              <button class="app-btn app-btn--sm app-btn--modal-cancel" @click="cancel">{{ cancelTitle }}</button>
              <button
                v-if="!okOnly"
                class="app-btn app-btn--sm app-btn--modal-cancel"
                @click="cancel"
              >{{ cancelTitle }}</button>
              <button
                class="app-btn app-btn--sm app-btn--modal-ok"
                :disabled="okDisabled"
                @click="ok"
              >{{ okTitle }}</button>
            </slot>
          </div>
        </div>
      </div>
    </transition>
</template>

<script>
export default {
  name: 'AppModal',
  inheritAttrs: false,
  model: { prop: 'modelValue', event: 'update:modelValue' },
  props: {
    modelValue: { type: Boolean, default: false },
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    size: { type: String, default: null },
    centered: { type: Boolean, default: true },
    scrollable: { type: Boolean, default: false },
    hideHeader: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    okTitle: { type: String, default: 'Aceptar' },
    cancelTitle: { type: String, default: 'Cancelar' },
    okVariant: { type: String, default: 'secondary' },
    cancelVariant: { type: String, default: 'outline-primary' },
    okDisabled: { type: Boolean, default: false },
    okOnly: { type: Boolean, default: false },
    noCloseOnBackdrop: { type: Boolean, default: false },
    noCloseOnEsc: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'ok', 'cancel', 'close', 'show', 'shown', 'hide', 'hidden'],
  data() {
    return { showModal: false, titleId: 'app-modal-' + Math.random().toString(36).substr(2, 9) }
  },
  computed: {
    isOpen() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    },
    modalClasses() {
      return {
        'app-modal--sm': this.size === 'sm',
        'app-modal--lg': this.size === 'lg',
        'app-modal--xl': this.size === 'xl',
        'app-modal--centered': this.centered
      }
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(val) {
        this.showModal = val
        if (val) {
          this.$emit('show')
          this.$nextTick(() => this.$emit('shown'))
          if (!this.noCloseOnEsc) document.addEventListener('keydown', this.onEsc)
        } else {
          if (!this.noCloseOnEsc) document.removeEventListener('keydown', this.onEsc)
        }
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onEsc)
  },
  methods: {
    close() {
      this.$emit('hide')
      this.showModal = false
      this.$emit('update:modelValue', false)
      this.$emit('close')
      this.$nextTick(() => this.$emit('hidden'))
      document.removeEventListener('keydown', this.onEsc)
    },
    onBackdropClick() {
      if (!this.noCloseOnBackdrop) this.close()
    },
    onEsc(e) {
      if (e.key === 'Escape') this.close()
    },
    ok() {
      this.$emit('ok')
      this.close()
    },
    cancel() {
      this.$emit('cancel')
      this.close()
    }
  }
}
</script>

<style scoped>
.app-modal-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  z-index: 1050;
  overflow-y: auto;
}
.app-modal {
  background: #fff;
  border-radius: 0.375rem;
  width: 500px;
  max-width: 100%;
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  pointer-events: auto;
}
.app-modal--centered { margin: auto; }
.app-modal--sm { width: 300px; }
.app-modal--lg { width: 800px; }
.app-modal--xl { width: 1140px; }

.app-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}
.app-modal__title { margin: 0; font-size: 1.25rem; line-height: 1.5; }
.app-modal__close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  opacity: 0.5;
  color: #000;
}
.app-modal__close:hover { opacity: 0.75; }

.app-modal__body { padding: 1rem; flex: 1; }
.app-modal__body--scrollable { max-height: 60vh; overflow-y: auto; }

.app-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0.75rem 1rem;
  border-top: 1px solid #dee2e6;
}

.app-btn--modal-cancel {
  background: transparent;
  border-color: #495057;
  color: #495057;
}
.app-btn--modal-cancel:hover { background: #495057; color: #fff; }

.app-btn--modal-ok {
  background: #3DF9DE;
  border-color: #0ce6c9;
  color: #212529;
}
.app-btn--modal-ok:hover { background: #0ce6c9; }

.app-modal-fade-enter-active,
.app-modal-fade-leave-active { transition: opacity 0.2s; }
.app-modal-fade-enter,
.app-modal-fade-leave-to { opacity: 0; }
</style>
